// static/Fluid_Schutff_Viscosity_Edition.xlsx
import {
  Dead_Oil_Options_Enum,
  Saturated_Oil_Options_Enum,
  Under_Saturated_Oil_Options_Enum
} from "gql/api";

import { oilViscositiesCalculator as beggsRobinson } from "./beggsRobinson";
import { oilViscositiesCalculator as deGhettoHeavyOil } from "./deGhettoHeavyOil";
import { oilViscositiesCalculator as glasoNorthSea } from "./glasoNorthSea";
import { oilViscositiesCalculator as kartoatmodjoSchmidt } from "./kartoatmodjoSchmidt";
import { oilViscositiesCalculator as custom } from "./custom";
import { deadOrLiveOilViscositiesCalculator } from "./custom/deadOrLiveOilViscosityCalculator";

import type {
  DeadOilViscosityCalculator,
  OilViscosityCorrelations,
  OilViscosityParameters,
  SaturatedOilViscosityCalculator,
  UnderSaturatedOilViscosityCalculator
} from "../d";
import type { Correlations } from "@/composables/forms/formTypes";
import { API } from "@/libs/units/density";
import type { BasicData } from "@/libs/calculations/fluidProperties/d";
import { Rankine } from "@/libs/units/temperature";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";

const methodMapDeadOilViscosity = {
  [Dead_Oil_Options_Enum.BeggsRobinson]: beggsRobinson,
  [Dead_Oil_Options_Enum.DeGhettoHeavyOil]: deGhettoHeavyOil,
  [Dead_Oil_Options_Enum.GlasoNorthSea]: glasoNorthSea,
  [Dead_Oil_Options_Enum.KartoatmodjoSchmidt]: kartoatmodjoSchmidt,
  [Dead_Oil_Options_Enum.Custom]: custom
};

const methodMapSaturatedOilViscosity = {
  [Saturated_Oil_Options_Enum.BeggsRobinson]: beggsRobinson,
  [Saturated_Oil_Options_Enum.DeGhettoHeavyOil]: deGhettoHeavyOil,
  [Saturated_Oil_Options_Enum.GlasoNorthSea]: glasoNorthSea,
  [Saturated_Oil_Options_Enum.KartoatmodjoSchmidt]: kartoatmodjoSchmidt
};

const methodMapUndersaturatedOilViscosity = {
  [Under_Saturated_Oil_Options_Enum.BeggsRobinson]: beggsRobinson,
  [Under_Saturated_Oil_Options_Enum.DeGhettoHeavyOil]: deGhettoHeavyOil,
  [Under_Saturated_Oil_Options_Enum.GlasoNorthSea]: glasoNorthSea,
  [Under_Saturated_Oil_Options_Enum.KartoatmodjoSchmidt]: kartoatmodjoSchmidt
};

export function getOilViscosityCorrelations(
  basicData: BasicData,
  gasOilRatio: number,
  correlations: Correlations,
  inSituPressurePSIA: number,
  temperatureF: number,
  condensateSpecificGravity: number,
  solutionGasRso: number,
  bubblePointPressurePSIA: number,
  customViscosityProperties: LagrangeInterpolationMapWithGroupings | null
): OilViscosityCorrelations {
  const temperatureInRankine = Rankine.toThisUnit(temperatureF);
  const tubingPressurePSIA = basicData.bottomTubingPressurePSIA;
  const reservoirPressurePSIA = basicData.staticBottomholePressurePSIA;
  const surfaceTemperatureF = basicData.surfaceTemperatureF;
  const datumTempF = basicData.bottomholeTemperatureF;
  const oilAPI = basicData.oilAPI;
  const gasSpecificGravity = basicData.gasSpecificGravity;
  const oilSpecificGravity = API.toStandardUnit(oilAPI);

  const params: OilViscosityParameters = {
    temperatureF,
    gasSpecificGravity,
    surfaceTemperatureF,
    datumTempF,
    tubingPressurePSIA,
    oilSpecificGravity,
    inSituPressurePSIA,
    oilAPI,
    solutionGasRso,
    bubblePointPressurePSIA,
    temperatureInRankine,
    reservoirPressurePSIA,
    gasOilRatio,
    condensateSpecificGravity,
    gasType: basicData.gasType,
    customViscosityProperties,
    sourceParams: {
      basicData,
      gasOilRatio,
      correlations,
      inSituPressurePSIA,
      temperatureF,
      bubblePointPressurePSIA
    }
  };
  const deadOilViscosityMethod =
    correlations?.dead_oil || Dead_Oil_Options_Enum.BeggsRobinson;
  const saturatedOilViscosityMethod =
    correlations?.saturated_oil || Saturated_Oil_Options_Enum.BeggsRobinson;
  const undersaturatedOilViscosityMethod =
    correlations?.under_saturated_oil ||
    Under_Saturated_Oil_Options_Enum.BeggsRobinson;

  const deadOilViscosityCalculator: DeadOilViscosityCalculator =
    methodMapDeadOilViscosity[deadOilViscosityMethod]
      .deadOilViscosityCalculator;

  const saturatedOilViscosityCalculator: SaturatedOilViscosityCalculator =
    methodMapSaturatedOilViscosity[saturatedOilViscosityMethod]
      .saturatedOilViscosityCalculator;

  const undersaturatedOilViscosityCalculator: UnderSaturatedOilViscosityCalculator =
    methodMapUndersaturatedOilViscosity[undersaturatedOilViscosityMethod]
      .underSaturatedOilViscosityCalculator;

  let deadOilViscosity: number = 0;
  let deadOilViscosityIntermediates;
  // This is liveOilViscosity
  let oilViscosity: number = 0;
  let saturatedOilViscosity: number = 0;
  let saturatedOilViscosityIntermediates;
  let undersaturatedOilViscosity: number = 0;
  let undersaturatedOilViscosityIntermediates;

  // Calculate dead oil viscosity (and its intermediates)
  ({ deadOilViscosity, ...deadOilViscosityIntermediates } =
    deadOilViscosityCalculator(params));

  // If custom live oil viscosity is provided, use that. Otherwise, calculate saturated and undersaturated oil viscosities.
  if (customViscosityProperties?.liveOilViscosityCP != null) {
    const { oilViscosity: calculatedOilViscosity, ...intermediates } =
      deadOrLiveOilViscositiesCalculator(params, "liveOilViscosityCP");
    oilViscosity = calculatedOilViscosity;
    undersaturatedOilViscosity = oilViscosity;
    undersaturatedOilViscosityIntermediates = { intermediates };
    saturatedOilViscosity = oilViscosity;
    saturatedOilViscosityIntermediates = { intermediates };
  } else {
    ({ saturatedOilViscosity, ...saturatedOilViscosityIntermediates } =
      saturatedOilViscosityCalculator({
        ...params,
        ...deadOilViscosityIntermediates,
        deadOilViscosity
      }));

    ({
      undersaturatedOilViscosity,
      ...undersaturatedOilViscosityIntermediates
    } = undersaturatedOilViscosityCalculator({
      ...params,
      ...deadOilViscosityIntermediates,
      ...saturatedOilViscosityIntermediates,
      deadOilViscosity,
      saturatedOilViscosity
    }));

    oilViscosity =
      params.inSituPressurePSIA > params.bubblePointPressurePSIA
        ? undersaturatedOilViscosity
        : saturatedOilViscosity;
  }

  return {
    deadOilViscosityCP: deadOilViscosity,
    saturatedOilViscosityCP: saturatedOilViscosity,
    understaturatedOilViscosityCP: undersaturatedOilViscosity,
    oilViscosityCP: oilViscosity,
    intermediates: {
      deadOilViscosity: deadOilViscosityIntermediates,
      saturatedOilViscosity: saturatedOilViscosityIntermediates,
      understaturatedOilViscosity: undersaturatedOilViscosityIntermediates
    },
    params
  };
}
